/**
 * How the menu groups the screens (Nav.tsx). Kept apart from screens.ts,
 * which imports every screen, so the grouping can be tested on its own.
 */

import type { IconName } from "./icons.js";

export type NavGroupId = "service" | "development" | "stock" | "reports" | "admin";

export interface NavGroup {
  readonly id: NavGroupId;
  readonly label: string;
  readonly icon: IconName;
  /** Its screens, in the order the menu lists them. */
  readonly kinds: readonly string[];
}

/**
 * How the menu groups the screens. The dashboard stands alone as home.
 *
 * Grouped by when and why somebody reaches for a screen, not by what it
 * stores, so a screen sits in every group where it is part of the job:
 * recipes are read during service and written in development, a stocktake
 * is the end of a shift and the start of an order, and expenses are both
 * deliveries coming in and money going out. Listing one twice costs a line;
 * hunting for it costs a shift.
 *
 * A group whose screens a person may not use does not appear for them.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  // "Drinks", not "Items": during service the book is a card index of what
  // can be poured. Development keeps the table, under its own name.
  { id: "service", label: "Service", icon: "service", kinds: ["queue", "whattail", "prep", "drinks", "training", "count", "waste", "cashup"] },
  { id: "development", label: "Development", icon: "develop", kinds: ["recipes", "chat", "wheel", "menu", "books"] },
  { id: "stock", label: "Stock", icon: "stockGroup", kinds: ["count", "order", "sup", "waste", "exp"] },
  { id: "reports", label: "Reports", icon: "reports", kinds: ["sales", "menu", "cashup", "exp", "guests"] },
  // Training is in both: a bartender works through it during service, a
  // manager writes it at the desk.
  { id: "admin", label: "Admin", icon: "admin", kinds: ["org", "books", "training", "platform"] },
];
